import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Radar, LayoutDashboard, Megaphone, MapPin, BarChart3, FileBarChart,
  CalendarDays, UserCircle, LogOut, Bell, Menu, X,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const NAV = [
  { to: "/portal", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/portal/campaigns", label: "Campaigns", icon: Megaphone },
  { to: "/portal/locations", label: "Locations", icon: MapPin },
  { to: "/portal/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/portal/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/portal/reports", label: "Reports", icon: FileBarChart },
  { to: "/portal/profile", label: "Profile", icon: UserCircle },
];

export default function ClientLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    api.get("/client/notifications").then((r) => setNotifications(r.data)).catch(() => {});
  }, []);

  const unread = notifications.filter((n) => !n.read).length;

  const markRead = (n) => {
    api.post(`/client/notifications/${n.id}/read`).catch(() => {});
    setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
  };

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  const sidebar = (suffix = "") => (
    <div className="flex flex-col h-full">
      <div className="px-5 h-[72px] flex items-center gap-2.5 border-b border-white/5">
        <span className="flex items-center justify-center w-9 h-9 rounded-md bg-blue-600/20 border border-blue-500/30">
          <Radar className="w-5 h-5 text-cyan-400" />
        </span>
        <span className="font-display font-bold text-white">GEOTRAQ <span className="text-cyan-400">AI</span></span>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1" data-testid={`client-nav${suffix}`}>
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMobileOpen(false)}
            data-testid={`client-nav-${n.label.toLowerCase()}${suffix}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                isActive ? "bg-blue-600/15 text-cyan-300 border border-blue-500/25" : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
              }`
            }>
            <n.icon className="w-4 h-4" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-white/5">
        <button onClick={doLogout} data-testid={`client-logout${suffix}`}
          className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-slate-400 hover:text-red-400 hover:bg-red-500/5 w-full transition-colors">
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex">
      <aside className="hidden lg:block w-60 shrink-0 border-r border-white/5 bg-[#060b16] fixed inset-y-0 z-40">{sidebar("")}</aside>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 inset-y-0 w-64 bg-[#060b16] border-r border-white/5">{sidebar("-mobile")}</aside>
        </div>
      )}

      <div className="flex-1 lg:ml-60 min-w-0">
        <header className="h-[72px] glass border-x-0 border-t-0 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-white p-1.5" onClick={() => setMobileOpen(!mobileOpen)} data-testid="client-mobile-menu" aria-label="Menu">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div>
              <p className="text-[10px] tracking-[0.2em] text-slate-500 uppercase">Client Portal</p>
              <h1 className="font-display font-bold text-white leading-tight" data-testid="client-name">{user?.profile?.company}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <button className="relative w-10 h-10 rounded-md border border-white/10 flex items-center justify-center text-slate-300 hover:text-white hover:border-cyan-500/40 transition-colors" data-testid="notifications-bell">
                  <Bell className="w-4 h-4" />
                  {unread > 0 && <span className="absolute -top-1 -right-1 w-4.5 h-4.5 min-w-[18px] px-1 rounded-full bg-cyan-500 text-[10px] font-bold text-background flex items-center justify-center" data-testid="notifications-count">{unread}</span>}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-80 bg-popover border-white/10 p-0" align="end" data-testid="notifications-panel">
                <p className="px-4 py-3 text-xs font-semibold tracking-widest text-slate-400 uppercase border-b border-white/5">Notifications</p>
                <div className="max-h-80 overflow-y-auto divide-y divide-white/5">
                  {notifications.length === 0 && <p className="p-4 text-sm text-slate-500">No notifications.</p>}
                  {notifications.map((n) => (
                    <button key={n.id} onClick={() => markRead(n)} className="w-full text-left px-4 py-3 hover:bg-white/[0.03] transition-colors" data-testid="notification-item">
                      <p className={`text-sm ${n.read ? "text-slate-400" : "text-white font-semibold"}`}>{n.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{n.message}</p>
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            <div className="w-10 h-10 rounded-md bg-blue-600/20 border border-blue-500/30 flex items-center justify-center font-display font-bold text-cyan-300 text-sm" data-testid="client-avatar">
              {(user?.profile?.company || "C")[0]}
            </div>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
